import { useQuery } from '@tanstack/react-query';
import { api } from '../../api/http';
import { Notice, errorMessage } from '../../components/common';
import { t, type MessageKey } from '../../i18n';
import { formatMinor } from '../../lib/money';

export interface Standing {
  currency: string;
  loans: {
    id: string;
    title: string;
    accessionNumber: string;
    dueOn: string;
    returnedOn: string | null;
    status: string;
    overdueDays: number;
  }[];
  fines: {
    id: string;
    title: string;
    reason: 'overdue' | 'lost' | 'damaged';
    amountMinor: number;
    status: 'unpaid' | 'paid' | 'waived';
  }[];
}

/** Loans and fines of one borrower; used for a staff member's own page and for a parent's child. */
export function StandingView({ standing }: { standing: Standing }) {
  return (
    <div className="space-y-4">
      <section aria-label={t('lib.myLoans')} className="space-y-2">
        <h2 className="text-lg font-semibold">{t('lib.myLoans')}</h2>
        {standing.loans.length === 0 && <p className="text-slate-600">{t('lib.noLoans')}</p>}
        <ul className="space-y-2 text-sm">
          {standing.loans.map((l) => (
            <li key={l.id} className="rounded-lg border border-slate-200 bg-white p-3">
              <span className="font-medium">{l.title}</span>{' '}
              <span className="font-mono text-xs">{l.accessionNumber}</span> ·{' '}
              {l.status === 'active' ? t('lib.dueOn', { date: l.dueOn }) : l.status}
              {l.overdueDays > 0 && (
                <span className="ml-2 font-medium text-red-700">
                  {t('lib.overdueDays', { days: l.overdueDays })}
                </span>
              )}
            </li>
          ))}
        </ul>
      </section>
      <section aria-label={t('lib.myFines')} className="space-y-2">
        <h2 className="text-lg font-semibold">{t('lib.myFines')}</h2>
        {standing.fines.length === 0 && <p className="text-slate-600">{t('lib.noFines')}</p>}
        <ul className="space-y-2 text-sm">
          {standing.fines.map((f) => (
            <li key={f.id} className="rounded-lg border border-slate-200 bg-white p-3">
              {t('lib.fineFor', {
                reason: t(`lib.reason.${f.reason}` as MessageKey),
                title: f.title,
              })}{' '}
              · <strong>{formatMinor(f.amountMinor, standing.currency)}</strong> ·{' '}
              {t(`lib.status.${f.status}` as MessageKey)}
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}

export function MyLibraryPage() {
  const mine = useQuery({
    queryKey: ['me', 'library'],
    queryFn: () => api<Standing>('/me/library'),
  });
  return (
    <div className="mx-auto max-w-3xl space-y-4">
      <h1 className="text-2xl font-bold">{t('nav.myLibrary')}</h1>
      {mine.isPending && <p role="status">{t('common.loading')}</p>}
      {mine.isError && <Notice kind="error">{errorMessage(mine.error)}</Notice>}
      {mine.data && <StandingView standing={mine.data} />}
    </div>
  );
}
